import { Box, Button, FormControl, InputLabel, MenuItem, Paper, Select, TextField } from '@mui/material'
import { FC, useCallback, useId } from 'react'
import { useTranslate } from 'react-admin'

import { INSPECTION_STATES } from '../../domain/activeInspection'
import type { ActiveInspectionFilters, TriState } from '../../domain/activeInspectionFilters'
import { countAppliedFilters, EMPTY_ACTIVE_INSPECTION_FILTERS } from '../../domain/activeInspectionFilters'
import { INSPECTION_LOCATIONS } from '../../domain/inspectionLocations'
import { MultiSelectFilter } from '../atoms/MultiSelectFilter'

const FIELD_WIDTH = 190

interface TriStateSelectProps {
  label: string
  value: TriState
  labels: Record<TriState, string>
  onChange: (value: TriState) => void
}

const TriStateSelect: FC<TriStateSelectProps> = ({ label, value, labels, onChange }) => {
  // MUI only points the control at its caption when the pair is wired by id.
  // Without it the Select reaches assistive technology unnamed, while the
  // Autocomplete-based filters beside it are named by their own TextField.
  const labelId = useId()

  return (
    <FormControl size="small" sx={{ width: FIELD_WIDTH }}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        value={value}
        label={label}
        onChange={event => {
          onChange(event.target.value as TriState)
        }}
      >
        <MenuItem value="ALL">{labels.ALL}</MenuItem>
        <MenuItem value="YES">{labels.YES}</MenuItem>
        <MenuItem value="NO">{labels.NO}</MenuItem>
      </Select>
    </FormControl>
  )
}

export interface InspectionFiltersProps {
  filters: ActiveInspectionFilters
  onChange: (filters: ActiveInspectionFilters) => void
}

/**
 * Filter bar for the inspections table. Every axis is applied in the browser,
 * including the derived ones (state, completeness), so stored and computed
 * columns filter alike.
 */
export const InspectionFilters: FC<InspectionFiltersProps> = ({ filters, onChange }) => {
  const translate = useTranslate()

  const update = useCallback(
    <K extends keyof ActiveInspectionFilters>(key: K, value: ActiveInspectionFilters[K]) => {
      onChange({ ...filters, [key]: value })
    },
    [filters, onChange],
  )

  const appliedCount = countAppliedFilters(filters)

  return (
    <Paper sx={{ p: 2, mb: 2 }} data-testid="inspection-filters">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          sx={{ width: FIELD_WIDTH }}
          label={translate('resources.inspections.fields.container')}
          value={filters.container}
          onChange={event => {
            update('container', event.target.value)
          }}
        />
        <MultiSelectFilter
          label={translate('resources.inspections.fields.location')}
          value={filters.locations}
          options={INSPECTION_LOCATIONS}
          getOptionLabel={location => translate(`resources.historical_inspections.enums.location.${location}`)}
          onChange={value => {
            update('locations', value)
          }}
        />
        <MultiSelectFilter
          label={translate('resources.inspections.fields.state')}
          value={filters.states}
          options={INSPECTION_STATES}
          getOptionLabel={state => translate(`resources.inspections.enums.state.${state}`)}
          onChange={value => {
            update('states', value)
          }}
        />
        <TriStateSelect
          label={translate('resources.inspections.fields.manual')}
          value={filters.manual}
          labels={{
            ALL: translate('resources.inspections.filters.any'),
            YES: translate('resources.inspections.enums.origin.MANUAL'),
            NO: translate('resources.inspections.enums.origin.AUTOMATIC'),
          }}
          onChange={value => {
            update('manual', value)
          }}
        />

        <Button
          size="small"
          disabled={appliedCount === 0}
          onClick={() => {
            onChange(EMPTY_ACTIVE_INSPECTION_FILTERS)
          }}
        >
          {translate('resources.inspections.filters.clear', { count: appliedCount })}
        </Button>
      </Box>
    </Paper>
  )
}
